import React, { Component } from 'react';
import { PropTypes } from 'prop-types';
import { asyncComponent } from 'react-async-component';

import LoadingWrapper from 'components/Common/LoadingWrapper';
import { GridHelper } from 'components/Grids/GridHelper';
import RtkColDefs from 'components/ReasonToKnowGrid/RtkColumnDefinitions';
import PagingButtons from 'components/Grids/PagingButtons';

/**
 * Async loader for AgGridReact component
 */
const AgGridReactLoading = () => (
  <LoadingWrapper loaded={ false } />
);
const AgGridReactError = ({ error }) => (
  <div>{ error.message }</div>
);
AgGridReactError.propTypes = {
  // eslint-disable-next-line react/forbid-prop-types
  error: PropTypes.any
};
AgGridReactError.defaultProps = {
  error: null
};
const AgGridReact = asyncComponent({
  resolve: () => import(/* webpackChunkName: "ag-grid-react" */ 'ag-grid-react').then(namespace => namespace.AgGridReact),
  LoadingComponent: AgGridReactLoading,
  ErrorComponent: AgGridReactError
});

export default class ReasonToKnowGrid extends Component {
  static propTypes = {
    permissions: PropTypes.arrayOf(PropTypes.string),
    isLoading: PropTypes.bool,
    rowData: PropTypes.arrayOf(PropTypes.shape({})),
    totalCount: PropTypes.number,
    onGridReady: PropTypes.func,
    changePageCallback: PropTypes.func.isRequired,
    takeItem: PropTypes.func.isRequired,
    pageNumber: PropTypes.number.isRequired,
    pageSize: PropTypes.number.isRequired,
    sortBy: PropTypes.string,
    sortDirection: PropTypes.string,
    filters: PropTypes.shape({}),
    showUserColumn: PropTypes.bool
  };

  static defaultProps = {
    permissions: null,
    isLoading: false,
    rowData: [],
    onGridReady: null,
    totalCount: 0,
    sortBy: null,
    sortDirection: null,
    filters: null,
    showUserColumn: false
  };

  constructor(props) {
    super(props);
    this.state = {
      gridApi: null,
      sortBy: props.sortBy,
      sortDirection: props.sortDirection,
      filters: props.filters,
      settingFiltersOnReload: false,
      gridOptions: GridHelper.buildGridOptions({
        pagination: false,
        rowClassRules: {
          'rtk-row-green': 'data.daysSinceRaised < 30',
          'rtk-row-amber': 'data.daysSinceRaised >= 30 && data.daysSinceRaised < 60',
          'rtk-row-red': 'data.daysSinceRaised >= 60'
        }
      })
    };
    this.changePageCallback = this.changePageCallback.bind(this);
    this.clearReloadFlag = this.clearReloadFlag.bind(this);
  }

  shouldComponentUpdate(nextProps, nextState) {
    const { isLoading, rowData, permissions, pageNumber, pageSize, sortBy, sortDirection, filters } = this.props;
    const { gridApi } = this.state;
    return !(
      isLoading === nextProps.isLoading
      && rowData === nextProps.rowData
      && permissions === nextProps.permissions
      && gridApi === nextState.gridApi
      && pageNumber === nextProps.pageNumber
      && pageSize === nextProps.pageSize
      && sortBy === nextProps.sortBy
      && sortDirection === nextProps.sortDirection
      && filters === nextProps.filters
      && permissions === nextProps.permissions
    );
  }

  static getDerivedStateFromProps(props, state) {
    return {
      sortBy: props.sortBy,
      sortDirection: props.sortDirection,
      filters: props.filters
    };
  }

  componentDidUpdate(prevProps, prevState) {
    const { isLoading, rowData } = this.props;
    const { gridApi, sortBy, sortDirection, filters, gridOptions } = this.state;

    if (gridApi) {
      if (isLoading) {
        gridApi.showLoadingOverlay();
      } else {
        gridApi.setRowData(rowData);
      }
    }

    if (gridOptions && sortBy && sortDirection) {
      const { columnApi } = gridOptions;
      if (columnApi && columnApi.getColumn(sortBy)) {
        columnApi.getColumn(sortBy).setSort(sortDirection);
      }
    }
    if (gridApi && filters) {
      gridApi.setFilterModel(filters);
    }
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.setGridSizeToFit.bind(this));
  }

  onFilterChanged() {
    this.checkOverlay();
  }

  setGridSizeToFit() {
    const { gridApi } = this.state;
    if (gridApi) {
      GridHelper.sizeColumnsToFit(gridApi);
      GridHelper.resetRowHeights(gridApi);
    }
  }

  setColumnDefs(colDefs) {
    const { gridApi } = this.state;
    if (gridApi) {
      gridApi.setColumnDefs(colDefs);
    }
  }

  getSortModel() {
    const { gridApi } = this.state;
    return (gridApi) ? gridApi.getSortModel() : null;
  }

  getFilterModel() {
    const { gridApi } = this.state;
    return (gridApi) ? gridApi.getFilterModel() : null;
  }

  resetPageNumber(pageNumber) {
    if (pageNumber) {
      this.pagingButtons.resetPageNumber(pageNumber);
    }
  }

  gridDidMount(gridApi) {
    window.addEventListener('resize', this.setGridSizeToFit.bind(this));
    this.setState({ gridApi });
    const { onGridReady } = this.props;
    if (onGridReady) {
      onGridReady();
    }
  }

  resizeRowHeights() {
    const { gridApi } = this.state;
    if (gridApi) {
      GridHelper.resetRowHeights(gridApi);
    }
  }

  checkOverlay() {
    const { gridApi } = this.state;
    const itemsCount = gridApi.getModel().getRowCount();
    if (itemsCount) {
      gridApi.hideOverlay();
    } else {
      gridApi.showNoRowsOverlay();
    }
  }

  searchInGrid(searchQuery) {
    const { gridApi } = this.state;
    if (gridApi) {
      gridApi.setQuickFilter(searchQuery);
    }
  }

  changePageCallback(paging) {
    const { changePageCallback } = this.props;
    const { gridApi } = this.state;

    if (paging && changePageCallback && changePageCallback(paging, gridApi.getSortModel(), gridApi.getFilterModel())) {
      return true;
    }
    return false;
  }

  gridReloaded() {
    const { gridApi, filters } = this.state;
    if (filters) {
      this.setState({ settingFiltersOnReload: true });
      gridApi.setFilterModel(filters);
    }
    if (!this.reloadTimer) {
      this.reloadTimer = setInterval(this.clearReloadFlag, 2000);
    }
    this.setGridSizeToFit();
    this.checkOverlay();
  }

  clearReloadFlag() {
    clearInterval(this.reloadTimer);
    this.reloadTimer = null;
    this.setState({ settingFiltersOnReload: false });
  }

  filterApplied() {
    //  Auto hide the column filter box
    const elem = document.querySelector('.ag-menu');
    if (elem) {
      elem.style.display = 'none';
    }

    this.reloadGrid();
  }

  reloadGrid() {
    const { changePageCallback, pageSize } = this.props;
    const { gridApi,
      settingFiltersOnReload,
      sortBy, sortDirection, filters } = this.state;

    if (settingFiltersOnReload) {
      return;
    }

    if (gridApi) {
      const sortModel = gridApi.getSortModel();
      const filterModel = gridApi.getFilterModel();
      let sortOrFilterChanged = false;

      if (sortModel && sortModel[0]
        && (sortModel[0].colId !== sortBy || sortModel[0].sort !== sortDirection)) {
        this.setState({
          sortBy: sortModel[0].colId,
          sortDirection: sortModel[0].sort
        });
        sortOrFilterChanged = true;
      } else if (sortBy !== '') {
        this.setState({
          sortBy: '',
          sortDirection: ''
        });
        sortOrFilterChanged = true;
      }

      if (filterModel && Object.keys(filterModel).length > 0
      && JSON.stringify(filterModel) !== JSON.stringify(filters)) {
        this.setState({
          filters: filterModel
        });

        sortOrFilterChanged = true;
      } else if ((filterModel && Object.keys(filterModel).length === 0)
      && (filters && Object.keys(filters).length !== 0)) {
        this.setState({
          filters: filterModel
        });

        sortOrFilterChanged = true;
      }

      if (sortOrFilterChanged) {
        changePageCallback({ number: 1, size: pageSize }, sortModel, filterModel);
      }
    }
  }

  render() {
    const {
      permissions,
      totalCount,
      pageSize,
      pageNumber,
      takeItem,
      showUserColumn,
      isLoading
    } = this.props;

    const columns = RtkColDefs.createColDefs(showUserColumn, permissions);

    const { gridOptions } = this.state;

    return (
      <div className="ag-custom">
        <AgGridReact
          gridOptions={ gridOptions }
          columnDefs={ columns }
          defaultColDef={ { autoHeight: true } }
          onGridReady={ params => this.gridDidMount(params.api) }
          onGridResize={ params => this.setGridSizeToFit(params.api) }
          onRowDataChanged={ () => this.gridReloaded() }
          onSortChanged={ () => this.reloadGrid() }
          onFilterChanged={ () => this.filterApplied() }
          context={ { permissions, takeItem } }
        />
        { !isLoading && <PagingButtons
          ref={ (ref) => { this.pagingButtons = ref; } }
          pageNumber={ pageNumber }
          pageSize={ pageSize }
          totalItems={ totalCount }
          changePageCallback={ this.changePageCallback }
        /> }
      </div>
    );
  }
}
